import {
  syncItemsAsync,
  syncAccountTypesAsync,
  syncAccountGroupsAsync,
  syncAccountsAsync,
  getSyncStatusAsync,
  initializeDatabase,
  EnrichedItem,
  LocalAccountType,
  LocalAccountGroup,
  LocalAccount
} from './database';
import axiosInstance from '@/app/api/axiosInstance';
import { useAuthStore } from '@/app/store/authStore';

interface SyncResult {
  success: boolean;
  itemCount: number;
  accountCount?: number;
  error?: string;
}

let syncInProgress = false;

// Sync enriched items from backend
export async function syncDataFromBackend(): Promise<SyncResult> {
  if (syncInProgress) {
    console.warn('[SyncManager] Sync already in progress');
    return { success: false, itemCount: 0, error: 'Sync already in progress' };
  }

  syncInProgress = true;

  try {
    const { userOrgId } = useAuthStore.getState();
    
    if (!userOrgId) {
      throw new Error('No organization selected');
    }

    console.log('[SyncManager] Starting data sync for organization:', userOrgId);

    // Initialize database if needed
    await initializeDatabase(userOrgId);

    // Fetch enriched items from backend
    const response = await axiosInstance.get<{ items: EnrichedItem[] }>('/items/sync', {
      params: {
        include_prices: true,
        include_tax: true,
        include_stock: true
      }
    });

    const items = response.data.items || [];
    console.log(`[SyncManager] Received ${items.length} enriched items from backend`);

    // Sync items to IndexedDB
    await syncItemsAsync(items, userOrgId);

    // Sync account types, groups, and accounts
    let accountCount = 0;
    try {
      accountCount = await syncAccountData(userOrgId);
    } catch (accountError) {
      console.error('[SyncManager] Account sync failed (non-fatal):', accountError);
      // Don't fail the entire sync if account sync fails
    }

    // Save sync timestamp
    localStorage.setItem('indexeddb_last_sync_time', new Date().toISOString());

    console.log('[SyncManager] Sync completed successfully');
    return { success: true, itemCount: items.length, accountCount };

  } catch (error) {
    console.error('[SyncManager] Sync failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, itemCount: 0, error: errorMessage };
  } finally {
    syncInProgress = false;
  }
}

// Sync account types, groups, and accounts
async function syncAccountData(organizationId: number): Promise<number> {
  console.log('[SyncManager] Syncing account data...');

  // Fetch account types (these are global)
  const typesResponse = await axiosInstance.get<{ items: LocalAccountType[] }>('/account-types/', {
    params: { limit: 100 }
  });
  const accountTypes = typesResponse.data.items || [];
  console.log(`[SyncManager] Received ${accountTypes.length} account types`);
  await syncAccountTypesAsync(accountTypes);

  // Fetch account groups
  const groupsResponse = await axiosInstance.get<{ items: LocalAccountGroup[] }>('/account-groups/', {
    params: { limit: 500 }
  });
  const accountGroups = (groupsResponse.data.items || []).map(group => ({
    ...group,
    organization_id: group.organization_id || organizationId
  }));
  console.log(`[SyncManager] Received ${accountGroups.length} account groups`);
  await syncAccountGroupsAsync(accountGroups, organizationId);

  // Fetch accounts
  const accountsResponse = await axiosInstance.get<{ items: LocalAccount[] }>('/accounts/', {
    params: { limit: 500 }
  });
  const accounts = (accountsResponse.data.items || []).map(account => ({
    ...account,
    organization_id: account.organization_id || organizationId
  }));
  console.log(`[SyncManager] Received ${accounts.length} accounts`);
  await syncAccountsAsync(accounts, organizationId);

  console.log('[SyncManager] Account data sync completed');
  return accounts.length;
}

// Get current sync status
export async function getSyncStatus(): Promise<{
  lastSync: Date | null;
  itemCount: number;
  status: 'success' | 'error' | 'pending';
  error?: string;
}> {
  try {
    const metadata = await getSyncStatusAsync();
    
    if (!metadata) {
      return {
        lastSync: null,
        itemCount: 0,
        status: 'pending'
      };
    }

    return {
      lastSync: metadata.last_sync,
      itemCount: metadata.item_count,
      status: metadata.status,
      error: metadata.error
    };
  } catch (error) {
    console.error('[SyncManager] Failed to get sync status:', error);
    return {
      lastSync: null,
      itemCount: 0,
      status: 'error',
      error: 'Failed to get sync status'
    };
  }
}

// Check if sync is needed (e.g., last sync was more than 1 hour ago)
export async function isSyncNeeded(): Promise<boolean> {
  const status = await getSyncStatus();
  
  if (!status.lastSync) {
    return true;
  }

  const hoursSinceSync = (Date.now() - status.lastSync.getTime()) / (1000 * 60 * 60);
  return hoursSinceSync > 1;
}

// Export sync state
export function isSyncInProgress(): boolean {
  return syncInProgress;
}